/**
 * Corelewear cart drawer helpers:
 * - <clw-cart-variant-edit>: change color / size / cup of a cart line in place.
 * - <clw-cart-upsell>: add a "frequently bought together" product in one tap.
 * Both add through /cart/add.js and dispatch CartLinesUpdateEvent with the
 * re-rendered cart sections, the same way the bundle offer block does, so the
 * drawer and the cart page refresh themselves.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const root = window.Shopify?.routes?.root || '/';

const sectionIds = () =>
  [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId);

const postJson = async (path, body) => {
  const response = await fetch(`${root}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart update failed');
  return data;
};

/**
 * Reads the post-add cart from the section HTML returned by /cart/add.js (snippets/cart-drawer.liquid
 * embeds it as `script[data-clw-cart]`), saving a sequential /cart.js round trip. Returns null when the
 * data is missing, malformed or lacks the added variants, so the caller falls back to /cart.js.
 * @param {Record<string, string> | undefined} sections
 * @param {{ id: number }[]} items
 */
const cartFromSections = (sections, items) => {
  try {
    for (const html of Object.values(sections || {})) {
      if (typeof html !== 'string') continue;
      const match = html.match(/<script[^>]*data-clw-cart[^>]*>([\s\S]*?)<\/script>/);
      if (!match) continue;
      const cart = JSON.parse(match[1]);
      if (!cart || !Array.isArray(cart.items) || !Number.isInteger(cart.item_count)) continue;
      const present = new Set(cart.items.map((line) => String(line.variant_id)));
      if (!items.every((item) => present.has(String(item.id)))) continue;
      CartLinesUpdateEvent.createCartFromAjaxResponse(cart); // throws on an unexpected shape
      return cart;
    }
  } catch (error) {
    // fall back to /cart.js
  }
  return null;
};

/**
 * Adds items, optionally after removing a line, and lets the theme re-render the cart.
 * @param {HTMLElement} source
 * @param {{ id: number, quantity: number }[]} items
 * @param {string} [removeKey] cart line key to remove first (variant swap)
 */
async function addToCart(source, items, removeKey) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    if (removeKey) await postJson('cart/change.js', { id: removeKey, quantity: 0 });
    const data = await postJson('cart/add.js', { items, sections: sectionIds().join(',') });
    const cart =
      cartFromSections(data.sections, items) ||
      (await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'corelewear-cart',
        sourceId: source.id,
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        sections: data.sections,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

const readVariants = (el) => {
  try {
    return JSON.parse(el.querySelector('[data-variants]')?.textContent || '[]');
  } catch (e) {
    return [];
  }
};

const findVariant = (variants, selects) => {
  const values = selects.map((select) => select.value);
  return variants.find((v) => v.options.every((value, i) => value === values[i]));
};

if (!customElements.get('clw-cart-variant-edit')) {
  customElements.define(
    'clw-cart-variant-edit',
    class extends HTMLElement {
      connectedCallback() {
        this.variants = readVariants(this);
        this.selects = [...this.querySelectorAll('select')];
        this.message = this.querySelector('[data-message]');
        this.panel = this.querySelector('[data-panel]');
        this.querySelector('[data-toggle]')?.addEventListener('click', () => this.toggle());
        this.selects.forEach((select) => select.addEventListener('change', () => this.onChange()));
      }

      toggle() {
        const open = this.panel.hidden;
        this.panel.hidden = !open;
        this.querySelector('[data-toggle]')?.setAttribute('aria-expanded', String(open));
      }

      say(text) {
        if (!this.message) return;
        this.message.textContent = text;
        this.message.hidden = !text;
      }

      async onChange() {
        const variant = findVariant(this.variants, this.selects);
        if (!variant) return this.say('This combination is not available.');
        if (String(variant.id) === this.dataset.variantId) return this.say('');
        if (!variant.available) return this.say('Sold out in this size. Try another one.');
        this.say('');
        this.setAttribute('aria-busy', 'true');
        this.selects.forEach((select) => (select.disabled = true));
        try {
          await addToCart(this, [{ id: variant.id, quantity: Number(this.dataset.quantity || 1) }], this.dataset.key);
        } catch (error) {
          this.say(error.message);
          this.selects.forEach((select) => (select.disabled = false));
        } finally {
          this.removeAttribute('aria-busy');
        }
      }
    }
  );
}

if (!customElements.get('clw-cart-upsell')) {
  customElements.define(
    'clw-cart-upsell',
    class extends HTMLElement {
      connectedCallback() {
        this.variants = readVariants(this);
        this.selects = [...this.querySelectorAll('select')];
        this.button = this.querySelector('[data-add]');
        this.message = this.querySelector('[data-message]');
        this.selects.forEach((select) => select.addEventListener('change', () => this.sync()));
        this.button?.addEventListener('click', () => this.add());
        this.sync();
      }

      sync() {
        const variant = findVariant(this.variants, this.selects);
        const ok = Boolean(variant?.available);
        if (this.button) this.button.disabled = !ok;
        if (this.message) {
          this.message.hidden = ok;
          this.message.textContent = ok ? '' : 'Sold out in this size';
        }
        return ok ? variant : null;
      }

      async add() {
        const variant = this.sync();
        if (!variant) return;
        this.button.setAttribute('aria-busy', 'true');
        try {
          await addToCart(this, [{ id: variant.id, quantity: 1 }]);
        } catch (error) {
          if (this.message) {
            this.message.textContent = error.message;
            this.message.hidden = false;
          }
        } finally {
          this.button.removeAttribute('aria-busy');
        }
      }
    }
  );
}
